/* eslint-disable no-await-in-loop */
import { createServer, ServerClient } from 'minecraft-protocol'
import { ParsedReplayPacket, parseReplayContents } from './parseReplayContents'
import { extractReplayEvents } from './extractReplayEvents'
import { PACKETS_REPLAY_FILE_EXTENSION, WORLD_STATE_FILE_EXTENSION } from 'mcraft-fun-mineflayer/build/worldState'
import MinecraftData from 'minecraft-data'
import { GameMode } from 'mineflayer'
import { Vec3 } from 'vec3'
import { UserError } from '../mineflayer/userError'
import { packetsReplayState, replayTimeIndex } from '../react/state/packetsReplayState'
import { formatBytes } from '../formatUnits'
import { appQueryParams } from '../appParams'
import { LocalServer } from '../customServer'
import { gameAdditionalState } from '../globalState'
import { options } from '../optionsStorage'
import { showErrorScreen } from '../react/errors/errorScreenState'

const SUPPORTED_FORMAT_VERSION = 1

const toRadians = (degrees: number) => degrees * Math.PI / 180
const fromNotchianYaw = (yaw: number) => {
  const twoPi = Math.PI * 2
  const value = Math.PI - toRadians(yaw)
  return ((value % twoPi) + twoPi) % twoPi
}
const fromNotchianPitch = (pitch: number) => {
  const twoPi = Math.PI * 2
  const value = -toRadians(pitch) + Math.PI
  return ((value % twoPi) + twoPi) % twoPi - Math.PI
}

let activeReplayInstanceId = 0

type ReplayDefinition = {
  minecraftVersion: string
  replayAgainst?: 'client' | 'server'
  serverIp?: string
}

interface OpenFileOptions {
  contents: string
  filename?: string
  filesize?: number
}

export function openFile ({ contents, filename = 'unnamed', filesize }: OpenFileOptions) {
  packetsReplayState.replayName = `${filename} (${formatBytes(filesize ?? contents.length)})`
  packetsReplayState.isRecording = false
  packetsReplayState.isMinimized = false
  packetsReplayState.isPlaying = false
  packetsReplayState.showPacketList = false
  packetsReplayState.seekTo = null
  if (typeof sessionStorage !== 'undefined' && sessionStorage.replaySeekTo) {
    const parsed = Number(sessionStorage.replaySeekTo)
    if (!Number.isNaN(parsed)) {
      packetsReplayState.seekTo = parsed
    }
    delete (sessionStorage as any).replaySeekTo
  }

  const connectOptions = {
    worldStateFileContents: contents,
    username: 'replay'
  }
  dispatchEvent(new CustomEvent('connect', { detail: connectOptions }))
}

export const startLocalReplayServer = async (contents: string) => {
  const { packets, header } = await parseReplayContents(contents)

  // Yield after parsing so the loading screen can render
  await new Promise(resolve => setTimeout(resolve, 0))

  packetsReplayState.replayEvents = await extractReplayEvents(packets)
  const lastTime = packets[packets.length - 1]?.time ?? 0
  packetsReplayState.totalTimeMs = lastTime

  const breakPackets = packets.filter(packet => typeof packet.name === 'string' && packet.name.includes('break'))
  if (breakPackets.length) {
    const counts = breakPackets.reduce((acc, packet) => {
      const key = `${packet.name}|${packet.state}|${packet.isFromServer ? 'server' : 'client'}`
      acc[key] = (acc[key] ?? 0) + 1
      return acc
    }, {} as Record<string, number>)
    console.log('[BREAK-DEBUG] replay break packets:', counts)
  } else {
    console.log('[BREAK-DEBUG] replay contains no break packets')
  }

  const replayInstanceId = ++activeReplayInstanceId

  const seekToFromPreviousState = packetsReplayState.seekTo
  let seekToFromSessionStorage: number | null = null
  let autoPlayFromSessionStorage = false
  let speedFromSessionStorage: number | null = null
  if (typeof sessionStorage !== 'undefined' && (sessionStorage as any).replaySeekTo) {
    const parsed = Number((sessionStorage as any).replaySeekTo)
    if (!Number.isNaN(parsed)) {
      seekToFromSessionStorage = parsed
    }
    delete (sessionStorage as any).replaySeekTo
  }

  if (typeof sessionStorage !== 'undefined' && (sessionStorage as any).replayAutoPlay) {
    autoPlayFromSessionStorage = true
    delete (sessionStorage as any).replayAutoPlay
  }

  if (typeof sessionStorage !== 'undefined' && (sessionStorage as any).replaySpeed) {
    const parsed = Number((sessionStorage as any).replaySpeed)
    if (!Number.isNaN(parsed) && parsed > 0) {
      speedFromSessionStorage = parsed
    }
    delete (sessionStorage as any).replaySpeed
  }

  packetsReplayState.packetsPlayback = []
  packetsReplayState.isOpen = true
  packetsReplayState.isRecording = false
  packetsReplayState.isMinimized = false
  packetsReplayState.isPlaying = autoPlayFromSessionStorage
  packetsReplayState.showPacketList = false
  packetsReplayState.replayMinecraftVersion = header.minecraftVersion
  packetsReplayState.seekTo = seekToFromSessionStorage ?? seekToFromPreviousState ?? null
  positions.client = 0
  positions.server = 0
  knownScoreboardObjectives.clear()
  const playPackets = packets.filter(p => p.state === 'play')
  replayTimeIndex.times = playPackets.map(p => p.time ?? 0)
  packetsReplayState.progress = {
    current: 0,
    total: playPackets.length
  }
  if (typeof packetsReplayState.seekTo === 'number') {
    packetsReplayState.seekTo = Math.max(0, Math.min(packetsReplayState.progress.total, packetsReplayState.seekTo))
  }
  if (speedFromSessionStorage !== null) {
    packetsReplayState.speed = speedFromSessionStorage
  }
  packetsReplayState.replayName ||= `local ${formatBytes(contents.length)}`
  if (!packetsReplayState.replayName.startsWith(`${header.minecraftVersion} `)) {
    packetsReplayState.replayName = `${header.minecraftVersion} ${packetsReplayState.replayName}`
  }

  if ('formatVersion' in header && header.formatVersion !== SUPPORTED_FORMAT_VERSION) {
    throw new UserError(`Unsupported format version: ${header.formatVersion}`)
  }
  if ('replayAgainst' in header && header.replayAgainst === 'server') {
    throw new Error('not supported')
  }

  const server = createServer({
    Server: LocalServer as any,
    version: header.minecraftVersion,
    keepAlive: false,
    'online-mode': false
  })

  const data = MinecraftData(header.minecraftVersion)
  server.on(data.supportFeature('hasConfigurationState') ? 'playerJoin' : 'login' as any, async client => {
    await mainPacketsReplayer(
      client,
      packets,
      packetsReplayState.customButtons.validateClientPackets.state ? undefined : true,
      replayInstanceId
    )
  })

  return {
    server,
    version: header.minecraftVersion
  }
}

// time based packets
// const FLATTEN_CLIENT_PACKETS = new Set(['position', 'position_look'])
const FLATTEN_CLIENT_PACKETS = new Set([] as string[])

const positions = {
  client: 0,
  server: 0
}

const MAX_LOGGED_PACKETS = 2000
const addPacketToReplayer: (name: string, data: any, isFromClient: boolean, wasUpcoming?: boolean, skipPlaybackLog?: boolean) => void = (name, data, isFromClient, wasUpcoming = false, skipPlaybackLog = false) => {
  const side = isFromClient ? 'client' : 'server'
  const shouldSkipPlaybackLog = skipPlaybackLog || !packetsReplayState.showPacketList

  if (wasUpcoming) {
    const lastUpcoming = packetsReplayState.packetsPlayback.find(p => p.isUpcoming && p.name === name)
    if (lastUpcoming) {
      lastUpcoming.isUpcoming = false
    }
  } else {
    if (!shouldSkipPlaybackLog) {
      packetsReplayState.packetsPlayback.push({
        name,
        data,
        isFromClient,
        position: ++positions[side]!,
        isUpcoming: false,
        timestamp: Date.now()
      })

      if (packetsReplayState.packetsPlayback.length > MAX_LOGGED_PACKETS) {
        packetsReplayState.packetsPlayback.splice(0, packetsReplayState.packetsPlayback.length - MAX_LOGGED_PACKETS)
      }
    } else {
      ++positions[side]!
    }
  }
}

const IGNORE_SERVER_PACKETS = new Set([
  'kick_disconnect',
])

const WORLD_REQUIRED_PACKETS = new Set([
  'map_chunk',
  'map_chunk_bulk',
  'unload_chunk',
  'update_light',
  'block_change',
  'multi_block_change',
  'chunk_batch_start',
  'chunk_batch_finish',
])

const knownScoreboardObjectives = new Set<string>()

const waitForBotWorld = async (timeoutMs = 5000) => {
  const start = Date.now()
  while (!(globalThis as any).bot?.world && Date.now() - start < timeoutMs) {
    await new Promise(resolve => setTimeout(resolve, 50))
  }
}

const waitForBotEntity = async (timeoutMs = 2000) => {
  const start = Date.now()
  while (!(globalThis as any).bot?.entity && Date.now() - start < timeoutMs) {
    await new Promise(resolve => setTimeout(resolve, 10))
  }
}

const getSyntheticLoginPacket = (version: string, packets: ParsedReplayPacket[]) => {
  const mcData = MinecraftData(version)
  const base = (mcData as any).loginPacket
  if (!base) return null

  const viewDistance = packets.find(p => p.isFromServer && p.state === 'play' && p.name === 'update_view_distance')?.params?.viewDistance
  const simulationDistance = packets.find(p => p.isFromServer && p.state === 'play' && p.name === 'simulation_distance')?.params?.distance

  const packet = { ...base }
  if (typeof viewDistance === 'number' && 'viewDistance' in packet) (packet as any).viewDistance = viewDistance
  if (typeof simulationDistance === 'number' && 'simulationDistance' in packet) (packet as any).simulationDistance = simulationDistance
  return packet
}

const ADDITIONAL_DELAY = 500

const SEEK_WARMUP_PACKETS = 3

let pendingReplayMovement: { x?: number, y?: number, z?: number, yaw?: number, pitch?: number } | null = null

const tryApplyReplayMovementToBot = (name: string, params: any, options?: { emitMove?: boolean }) => {
  if (!(globalThis as any).bot) return
  const bot = (globalThis as any).bot as typeof __type_bot
  const emitMove = options?.emitMove ?? true
  if (!bot?.entity) {
    if (name === 'position' || name === 'position_look' || name === 'look') {
      pendingReplayMovement = {
        x: typeof params?.x === 'number' ? params.x : pendingReplayMovement?.x,
        y: typeof params?.y === 'number' ? params.y : pendingReplayMovement?.y,
        z: typeof params?.z === 'number' ? params.z : pendingReplayMovement?.z,
        yaw: typeof params?.yaw === 'number' ? params.yaw : pendingReplayMovement?.yaw,
        pitch: typeof params?.pitch === 'number' ? params.pitch : pendingReplayMovement?.pitch,
      }
    }
    return
  }

  const applyPending = () => {
    if (!pendingReplayMovement) return
    const m = pendingReplayMovement
    pendingReplayMovement = null
    tryApplyReplayMovementToBot('position_look', m)
  }
  applyPending()

  // Handle arm swing animation from client packets (skip during seeking)
  if (name === 'arm_animation') {
    if (emitMove) {
      const hand = params?.hand === 1 ? 'left' : 'right'
      ;(bot as any).emit('botArmSwingStart', hand)
      setTimeout(() => {
        ;(bot as any).emit('botArmSwingEnd', hand)
      }, 250)
    }
    return
  }

  // Handle sneaking/sprinting from client entity_action packets
  if (name === 'entity_action') {
    const actionId = params?.actionId
    if (actionId === 0) {
      gameAdditionalState.isSneaking = true
      bot.controlState.sneak = true
    }
    if (actionId === 1) {
      gameAdditionalState.isSneaking = false
      bot.controlState.sneak = false
    }
    if (actionId === 3) gameAdditionalState.isSprinting = true
    if (actionId === 4) gameAdditionalState.isSprinting = false
    return
  }

  // Handle held item slot change from client packets
  if (name === 'held_item_slot') {
    const slotId = params?.slotId
    if (typeof slotId === 'number' && slotId >= 0 && slotId <= 8) {
      bot.quickBarSlot = slotId
      ;(bot as any).emit('heldItemChanged')
    }
    return
  }

  if (name !== 'position' && name !== 'position_look' && name !== 'look') return

  if (typeof params?.x === 'number' && typeof params?.y === 'number' && typeof params?.z === 'number') {
    // mineflayer expects Vec3
    bot.entity.position = new Vec3(params.x, params.y, params.z)
  }
  if (typeof params?.onGround === 'boolean') {
    bot.entity.onGround = params.onGround
  }

  // Recorded yaw/pitch values are in Notchian (protocol) format degrees.
  // Convert to mineflayer's internal convention (fromNotchianYaw/fromNotchianPitch).
  if (typeof params?.yaw === 'number') {
    bot.entity.yaw = fromNotchianYaw(params.yaw)
  }
  if (typeof params?.pitch === 'number') {
    bot.entity.pitch = fromNotchianPitch(params.pitch)
  }

  if (emitMove) {
    ;(bot as any).emit('move')
    // Notify renderer to update player entity model (for third-person view smoothing)
    appViewer?.worldView?.emit('playerEntity', bot.entity)
  }
}

// Merges the recorded client movement packets into one time-sorted camera path, in
// mineflayer's angle convention (the values the reactive path feeds via bot.entity.yaw/pitch).
// Yaw is unwrapped so the spline never spins the long way around across the ±π seam.
const buildCameraTimeline = (playPackets: ParsedReplayPacket[]) => {
  const pts: Array<{ t: number, x: number, y: number, z: number, yaw: number, pitch: number }> = []
  let x: number | undefined; let y: number | undefined; let z: number | undefined
  let yaw = 0; let pitch = 0
  let prevYaw = 0; let yawInit = false
  const twoPi = Math.PI * 2
  for (const p of playPackets) {
    if (p.isFromServer) continue
    if (p.name !== 'position' && p.name !== 'position_look' && p.name !== 'look') continue
    const pr = p.params
    if (typeof pr?.x === 'number' && typeof pr?.y === 'number' && typeof pr?.z === 'number') {
      x = pr.x; y = pr.y; z = pr.z
    }
    if (typeof pr?.yaw === 'number') {
      let my = fromNotchianYaw(pr.yaw)
      if (yawInit) {
        while (my - prevYaw > Math.PI) my -= twoPi
        while (my - prevYaw < -Math.PI) my += twoPi
      }
      yaw = my; prevYaw = my; yawInit = true
    }
    if (typeof pr?.pitch === 'number') pitch = fromNotchianPitch(pr.pitch)
    if (x === undefined || y === undefined || z === undefined) continue
    pts.push({ t: p.time ?? 0, x, y, z, yaw, pitch })
  }
  return pts
}

// Reconstructs each entity's absolute position path by replaying the server movement
// packets the way mineflayer does: absolute for spawn/teleport/sync, +dX/dY/dZ ÷4096 for
// relative moves. Entity updates are recorded at only ~3.3 Hz, so the renderer needs the
// whole path to glide across the gaps instead of freezing then teleporting.
const ENTITY_ABS_PACKETS = new Set(['entity_teleport', 'teleport_entity', 'sync_entity_position'])
const ENTITY_REL_PACKETS = new Set(['rel_entity_move', 'entity_move_look'])
const ENTITY_SPAWN_PACKETS = new Set(['spawn_entity', 'named_entity_spawn'])
const buildEntityTimelines = (playPackets: ParsedReplayPacket[]) => {
  const map = new Map<number, Array<{ t: number, x: number, y: number, z: number, yaw: number, pitch: number }>>()
  const cur = new Map<number, { x: number, y: number, z: number }>()
  const push = (id: number, t: number, x: number, y: number, z: number) => {
    let arr = map.get(id); if (!arr) { arr = []; map.set(id, arr) }
    arr.push({ t, x, y, z, yaw: 0, pitch: 0 })
  }
  for (const p of playPackets) {
    if (!p.isFromServer) continue
    const pr = p.params
    // Drop reconstruction state on despawn so a reused entity id doesn't accumulate
    // relative moves from a previous entity's stale position.
    if (p.name === 'entity_destroy') {
      const ids = pr?.entityIds ?? (typeof pr?.entityId === 'number' ? [pr.entityId] : [])
      for (const d of ids) cur.delete(d)
      continue
    }
    const id = pr?.entityId
    if (typeof id !== 'number') continue
    const t = p.time ?? 0
    if (ENTITY_SPAWN_PACKETS.has(p.name) || ENTITY_ABS_PACKETS.has(p.name)) {
      if (typeof pr.x !== 'number' || typeof pr.y !== 'number' || typeof pr.z !== 'number') continue
      cur.set(id, { x: pr.x, y: pr.y, z: pr.z })
      push(id, t, pr.x, pr.y, pr.z)
    } else if (ENTITY_REL_PACKETS.has(p.name)) {
      const c = cur.get(id); if (!c) continue // no absolute seed yet
      c.x += (pr.dX ?? 0) / 4096; c.y += (pr.dY ?? 0) / 4096; c.z += (pr.dZ ?? 0) / 4096
      push(id, t, c.x, c.y, c.z)
    }
  }
  for (const [id, arr] of map) if (arr.length < 2) map.delete(id)
  return map
}

const mainPacketsReplayer = async (client: ServerClient, packets: ParsedReplayPacket[], ignoreClientPacketsWait: string[] | true = [], replayInstanceId: number) => {
  // Disable mineflayer's built-in physics so it doesn't override our direct position sets
  if ((globalThis as any).bot) {
    (globalThis as any).bot.physicsEnabled = false
  }

  // Force entity rendering on in replay mode (user setting may have it disabled)
  options.renderEntities = true

  let clientClosed = false
  let manualPhysicsTimer: ReturnType<typeof setInterval> | null = null
  const markClientClosed = () => {
    clientClosed = true
  }
  client.once?.('end' as any, markClientClosed)
  client.once?.('close' as any, markClientClosed)
  client.once?.('error' as any, markClientClosed)

  const isStale = () => replayInstanceId !== activeReplayInstanceId

  if ((globalThis as any).bot) {
    manualPhysicsTimer = setInterval(() => {
      const bot = (globalThis as any).bot as typeof __type_bot | undefined
      if (!bot?.world || !bot?.entity) return
      if (!packetsReplayState.isPlaying) return
      bot.emit('physicsTick')
    }, 50)
  }

  const nextFrame = async () => new Promise<void>(resolve => {
    requestAnimationFrame(() => { setTimeout(resolve, 0) })
  })

  const writePacket = (name: string, data: any) => {
    data = restoreData(data)
    client.write(name, data)
  }

  const playPackets = packets.filter(p => p.state === 'play')

  // Handing over the full paths up front lets the renderer interpolate without the render
  // delay a live-style buffer would need (see setReplayCameraTimeline).
  ;(appViewer?.backend as any)?.setReplayCameraTimeline?.(buildCameraTimeline(playPackets))
  ;(appViewer?.backend as any)?.setReplayEntityTimelines?.(buildEntityTimelines(playPackets))

  if (!isStale()) {
    replayTimeIndex.times = playPackets.map(p => p.time ?? 0)
    packetsReplayState.progress.total = playPackets.length
    packetsReplayState.progress.current = Math.min(packetsReplayState.progress.current, packetsReplayState.progress.total)
    if (typeof packetsReplayState.seekTo === 'number') {
      packetsReplayState.seekTo = Math.max(0, Math.min(packetsReplayState.progress.total, packetsReplayState.seekTo))
    }
  }

  let wasSeeking = false
  let seekWarmupRemaining = 0
  let isRewind = false
  let skipLoginOnThisPass = false
  let lastYieldTime = performance.now()

  let timelineWallAnchor = performance.now()
  let timelinePacketAnchor = 0 // recorded ms that timelineWallAnchor corresponds to
  let timelineSpeed = packetsReplayState.speed
  let timelineNeedsReset = true
  const TIMER_FLOOR_MS = 4

  const MAX_TIMELINE_LAG_MS = 1000
  const resetTimeline = (packetTime: number) => {
    timelineWallAnchor = performance.now()
    timelinePacketAnchor = packetTime
    timelineSpeed = packetsReplayState.speed
    timelineNeedsReset = false
  }

  const buildRespawnFromLogin = (lp: any): any => {
    const r: any = { copyMetadata: false }
    const fields = ['dimension', 'worldName', 'hashedSeed', 'gameMode', 'previousGameMode', 'isDebug', 'isFlat',
      'dimensionCodec', 'difficulty', 'levelType', 'viewDistance', 'maxPlayers', 'reducedDebugInfo',
      'enableRespawnScreen', 'isHardcore', 'isDebug', 'simulationDistance']
    for (const f of fields) {
      if (lp[f] !== undefined) r[f] = lp[f]
    }
    if (r.previousGameMode === undefined && r.gameMode !== undefined) r.previousGameMode = r.gameMode
    return r
  }

  let clientPackets = [] as Array<{ name: string, params: any }>
  const clientsPacketsWaiter = createPacketsWaiter({
    unexpectedPacketReceived (name, params) {
      addPacketToReplayer(name, params, true)
    },
    expectedPacketReceived (name, params) {
      addPacketToReplayer(name, params, true, true)
    },
    unexpectedPacketsLimit: 15,
    onUnexpectedPacketsLimitReached () {
      addPacketToReplayer('...', {}, true)
    }
  })

  // A few unplayable packets are normal in older recordings, so a single failure
  // must not interrupt playback; a flood of them means the replay is genuinely
  // broken and has to surface instead of only filling the console.
  const PLAYBACK_ERROR_LIMIT = 25
  let playbackErrors = 0

  // Patch console.error to detect errors
  const originalConsoleError = console.error
  let lastSentPacket: { name: string, params: any } | null = null
  console.error = (...args) => {
    if (lastSentPacket) {
      console.log('Got error after packet', lastSentPacket.name, lastSentPacket.params)
    }
    originalConsoleError.apply(console, args)
    if (packetsReplayState.customButtons.stopOnError.state) {
      packetsReplayState.isPlaying = false
      throw new Error('Replay stopped due to error: ' + args.join(' '))
    }
  }

  const playServerPacket = async (name: string, params: any) => {
    if (name === 'block_break_animation') {
      console.log('[BREAK-DEBUG] playServerPacket block_break_animation:', JSON.stringify(params))
    }
    try {
      if (name === 'scoreboard_objective' && params?.name) {
        if (params.action === 0) {
          knownScoreboardObjectives.add(params.name)
        } else if (params.action === 1) {
          knownScoreboardObjectives.delete(params.name)
        } else if (params.action === 2 && !knownScoreboardObjectives.has(params.name)) {
          params = { ...params, action: 0 }
          knownScoreboardObjectives.add(params.name)
        }
      }

      if (WORLD_REQUIRED_PACKETS.has(name) && !(globalThis as any).bot?.world) {
        await waitForBotWorld(1000)
      }

      if (WORLD_REQUIRED_PACKETS.has(name) && !(globalThis as any).bot?.world) {
        addPacketToReplayer(name, params, false, false, wasSeeking)
        lastSentPacket = { name, params }
        return
      }
      writePacket(name, params)
      addPacketToReplayer(name, params, false, false, wasSeeking)
      lastSentPacket = { name, params }
    } catch (err) {
      console.error('Error processing packet:', err)
      playbackErrors++
      // fires exactly once per replay session, on crossing the limit
      if (playbackErrors === PLAYBACK_ERROR_LIMIT && !isStale()) {
        packetsReplayState.isPlaying = false
        showErrorScreen('playback', `${playbackErrors} packet errors, last on '${name}': ${err instanceof Error ? err.message : String(err)}`)
      }
      if (packetsReplayState.customButtons.stopOnError.state) {
        packetsReplayState.isPlaying = false
      }
    }
  }

  try {
    bot.on('error', (err) => {
      console.error('Mineflayer error:', err)
    })

    bot._client.on('writePacket' as any, (name, params) => {
      clientsPacketsWaiter.addPacket(name, params)
    })

    bot.once('spawn', () => {
      if (pendingReplayMovement) {
        tryApplyReplayMovementToBot('position_look', pendingReplayMovement)
      }
    })

    const hasLoginPacket = playPackets.some(p => p.isFromServer && p.name === 'login')
    if (!hasLoginPacket) {
      try {
        const syntheticLogin = getSyntheticLoginPacket(bot.version, packets)
        if (syntheticLogin) {
          writePacket('login', syntheticLogin)
        }
      } catch (err) {
        console.warn('Failed to send synthetic login packet:', err)
      }
    }

    const hasPositionPacket = playPackets.some(p => p.isFromServer && p.name === 'position')
    let pendingSyntheticPosition: (() => void) | null = null
    if (!hasPositionPacket) {
      const firstClientPosition = playPackets.find(p => !p.isFromServer && (p.name === 'position' || p.name === 'position_look'))?.params
      if (firstClientPosition && typeof firstClientPosition.x === 'number' && typeof firstClientPosition.y === 'number' && typeof firstClientPosition.z === 'number') {
        const basePositionPacket: any = {
          x: firstClientPosition.x,
          y: firstClientPosition.y,
          z: firstClientPosition.z,
          yaw: typeof firstClientPosition.yaw === 'number' ? firstClientPosition.yaw : 0,
          pitch: typeof firstClientPosition.pitch === 'number' ? firstClientPosition.pitch : 0,
          flags: 0,
          teleportId: 1,
          dismountVehicle: false,
        }

        const candidates: any[] = [
          basePositionPacket,
          (({ dismountVehicle, ...rest }) => rest)(basePositionPacket),
          (({ teleportId, dismountVehicle, ...rest }) => rest)(basePositionPacket),
        ]

        const sendSyntheticPosition = () => {
          let lastError: unknown
          let sent = false
          for (const candidate of candidates) {
            try {
              writePacket('position', candidate)
              sent = true
              break
            } catch (err) {
              lastError = err
            }
          }
          if (!sent) {
            console.warn('Failed to send synthetic position packet:', lastError)
          }
        }

        if (!hasLoginPacket) {
          // Synthetic login was sent above; bot.entity is being created in the next event-loop tick.
          // Wait for it before sending synthetic position so mineflayer's position handler
          // (bot.entity.height = 1.8) doesn't crash on undefined entity.
          await waitForBotEntity(2000)
          sendSyntheticPosition()
        } else {
          // Real login packet is queued in playPackets and will be processed in the main loop.
          // Defer synthetic position until after the first server-side login is dispatched.
          pendingSyntheticPosition = sendSyntheticPosition
        }
      }
    }

    console.log('start replaying!')
    // eslint-disable-next-line no-labels
    rewindLoop: while (!clientClosed && !isStale()) {
      if (!isRewind) {
        await waitForBotWorld(5000)
      } else {
        knownScoreboardObjectives.clear()
        packetsReplayState.packetsPlayback = []
        positions.client = 0
        positions.server = 0
        wasSeeking = true
        seekWarmupRemaining = 0
        clientPackets = []
        clientsPacketsWaiter.stopWaiting()
        skipLoginOnThisPass = true
        if (!isStale()) packetsReplayState.progress.current = 0
        const loginPacket = playPackets.find(p => p.isFromServer && p.name === 'login')
        if (loginPacket?.params) {
          try {
            writePacket('respawn', buildRespawnFromLogin(loginPacket.params))
          } catch (e) {
            console.warn('[Rewind] synthetic respawn failed:', e)
          }
        }
        await new Promise(resolve => setTimeout(resolve, 50))
        isRewind = false
      }

      for (const [i, packet] of playPackets.entries()) {
        if (clientClosed || isStale()) break

        if (packetsReplayState.internalRewindTo !== null) {
          packetsReplayState.seekTo = packetsReplayState.internalRewindTo
          packetsReplayState.isSeeking = true
          packetsReplayState.internalRewindTo = null
          isRewind = true
          clientsPacketsWaiter.stopWaiting()
          break
        }

        if (skipLoginOnThisPass && packet.isFromServer && packet.name === 'login') {
          skipLoginOnThisPass = false
          continue
        }

        const seekTo = packetsReplayState.seekTo
      const isSeekingForward = typeof seekTo === 'number' && seekTo > packetsReplayState.progress.current

      if (typeof seekTo === 'number' && seekTo <= packetsReplayState.progress.current) {
        packetsReplayState.seekTo = null
      }

      if (isSeekingForward && !wasSeeking) {
        packetsReplayState.packetsPlayback = []
        positions.client = 0
        positions.server = 0
        wasSeeking = true
        packetsReplayState.isSeeking = true
        // Let the loader paint before the loop starts hogging the main thread.
        await nextFrame()
        lastYieldTime = performance.now()
      }
      if (!isSeekingForward && wasSeeking) {
        wasSeeking = false
        packetsReplayState.isSeeking = false
        if (!packetsReplayState.isPlaying) {
          seekWarmupRemaining = SEEK_WARMUP_PACKETS
        }
        if ((globalThis as any).bot?.entity) {
          ;((globalThis as any).bot as any).emit('move')
        }
      }

      const isWarmingUpAfterSeek = seekWarmupRemaining > 0 && !isSeekingForward

      if (!packetsReplayState.isPlaying && !isSeekingForward && !isWarmingUpAfterSeek) {
        // Freeze the zero-lag camera at its current position while paused.
        ;(appViewer?.backend as any)?.pauseReplayCameraClock?.()
        await new Promise<void>(resolve => {
          const interval = setInterval(() => {
            if (clientClosed || isStale()) {
              clearInterval(interval)
              resolve()
              return
            }
            if (packetsReplayState.isPlaying || packetsReplayState.internalRewindTo !== null || (typeof packetsReplayState.seekTo === 'number' && packetsReplayState.seekTo > packetsReplayState.progress.current)) {
              clearInterval(interval)
              resolve()
            }
          }, 100)
        })
        // Wall time spent paused is not playback lateness.
        timelineNeedsReset = true
      }

      if (clientClosed || isStale()) break

      // Between packets the renderer extrapolates at `rate` (0 = frozen), so motion stays
      // smooth across silent gaps in the recording.
      ;(appViewer?.backend as any)?.setReplayCameraClock?.(
        packet.time ?? 0,
        (packetsReplayState.isPlaying && !isSeekingForward && !isWarmingUpAfterSeek) ? packetsReplayState.speed : 0
      )

      try {
        if (packet.isFromServer) {
          if (packet.params === null) {
            console.warn('packet.params is null', packet)
            continue
          }
          await playServerPacket(packet.name, packet.params)
          if (pendingSyntheticPosition && packet.name === 'login') {
            // Real login was just dispatched. Wait for bot.entity to be created
            // (mineflayer's login handler runs on the next event-loop tick),
            // then send the deferred synthetic position so the bot has a starting pose.
            await waitForBotEntity(2000)
            try {
              pendingSyntheticPosition()
            } catch (err) {
              console.warn('Failed to send deferred synthetic position:', err)
            }
            pendingSyntheticPosition = null
          }
        } else {
          // Apply recorded client movement/look so replay shows player actions.
          tryApplyReplayMovementToBot(packet.name, packet.params, { emitMove: !isSeekingForward })

          if (!isSeekingForward && ignoreClientPacketsWait !== true && !ignoreClientPacketsWait.includes(packet.name)) {
            clientPackets.push({ name: packet.name, params: packet.params })
            if (playPackets[i + 1]?.isFromServer) {
              // eslint-disable-next-line @typescript-eslint/no-loop-func
              clientPackets = clientPackets.filter((p, index) => {
                return !FLATTEN_CLIENT_PACKETS.has(p.name) || index === clientPackets.findIndex(clientPacket => clientPacket.name === p.name)
              })
              for (const packet of clientPackets) {
                packetsReplayState.packetsPlayback.push({
                  name: packet.name,
                  data: packet.params,
                  isFromClient: true,
                  position: positions.client++,
                  timestamp: Date.now(),
                  isUpcoming: true,
                })
              }

              await Promise.race([
                clientsPacketsWaiter.waitForPackets(clientPackets.map(p => p.name)),
                ...(packetsReplayState.customButtons.skipMissingOnTimeout.state ? [new Promise(resolve => {
                  setTimeout(resolve, 1000)
                })] : [])
              ])
              clientsPacketsWaiter.stopWaiting()
              clientPackets = []
            }
          }
        }
      } finally {
        if (!clientClosed && !isStale()) {
          const total = packetsReplayState.progress.total
          const next = packetsReplayState.progress.current + 1
          packetsReplayState.progress.current = total ? Math.min(next, total) : next
        }
        if (isWarmingUpAfterSeek) {
          seekWarmupRemaining--
        }
      }

      if (isSeekingForward || isWarmingUpAfterSeek || !packetsReplayState.isPlaying) {
        timelineNeedsReset = true
      } else if (packet.diff) {
        const packetTime = packet.time ?? 0
        if (timelineNeedsReset || packetsReplayState.speed !== timelineSpeed) resetTimeline(packetTime)

        const extraDelay = packet.isFromServer ? ADDITIONAL_DELAY * (packetsReplayState.customButtons.packetsSenderDelay.state ? 1 : 0) : 0
        if (extraDelay) timelineWallAnchor += extraDelay / packetsReplayState.speed

        const dueWall = timelineWallAnchor + (packetTime - timelinePacketAnchor) / packetsReplayState.speed
        const remaining = dueWall - performance.now()
        if (remaining >= TIMER_FLOOR_MS) {
          await new Promise(resolve => setTimeout(resolve, remaining))
          lastYieldTime = performance.now()
        } else if (remaining < -MAX_TIMELINE_LAG_MS) {
          resetTimeline(packetTime)
        }
      }

      // Yield to the browser when we've been on the main thread >threshold without a pause.
      // map_chunk packets can each take 5-30ms to parse, creating large synchronous
      // bursts even when timestamps are spread out. Time-based yielding ensures
      // the renderer gets a frame budget regardless of packet type or count.
      // While seeking the game is behind the loader, so frames buy nothing but cost
      // real time — yield just often enough to keep the tab (and spinner) alive.
      const yieldThreshold = isSeekingForward ? 250 : 12
      if (performance.now() - lastYieldTime > yieldThreshold) {
        lastYieldTime = performance.now()
        await new Promise(resolve => setTimeout(resolve, 0))
      }
      }  // end for-loop

      // eslint-disable-next-line no-labels
      if (isRewind) continue rewindLoop

      if (!clientClosed && !isStale()) {
        packetsReplayState.isPlaying = false
        await new Promise<void>(resolve => {
          const interval = setInterval(() => {
            if (clientClosed || isStale() || packetsReplayState.internalRewindTo !== null) {
              clearInterval(interval)
              resolve()
            }
          }, 100)
        })
        if (!clientClosed && !isStale() && packetsReplayState.internalRewindTo !== null) {
          isRewind = true
          // eslint-disable-next-line no-labels
          continue rewindLoop
        }
      }
      break rewindLoop
    }  // end rewindLoop while
  } finally {
    packetsReplayState.isSeeking = false
    if (manualPhysicsTimer) {
      clearInterval(manualPhysicsTimer)
    }
    // Restore original console.error
    console.error = originalConsoleError
  }
}

export const switchGameMode = (gameMode: GameMode) => {
  const gamemodes = {
    survival: 0,
    creative: 1,
    adventure: 2,
    spectator: 3
  }
  if (gameMode === 'spectator') {
    bot._client.emit('abilities', {
    // can fly + is flying
      flags: 6
    })
  }
  bot._client.emit('game_state_change', {
    reason: 3,
    gameMode: gamemodes[gameMode]
  })
}

interface PacketsWaiterOptions {
  unexpectedPacketReceived?: (name: string, params: any) => void
  expectedPacketReceived?: (name: string, params: any) => void
  onUnexpectedPacketsLimitReached?: () => void
  unexpectedPacketsLimit?: number
}

interface PacketsWaiter {
  addPacket(name: string, params: any): void
  waitForPackets(packets: string[]): Promise<void>
  stopWaiting(): void
}

const createPacketsWaiter = (options: PacketsWaiterOptions = {}): PacketsWaiter => {
  let packetHandler: ((data: any, name: string) => void) | null = null
  const queuedPackets: Array<{ name: string, params: any }> = []
  let isWaiting = false
  let unexpectedPacketsCount = 0
  const handlePacket = (data: any, name: string, waitingPackets: string[], resolve: () => void) => {
    if (waitingPackets.includes(name)) {
      waitingPackets.splice(waitingPackets.indexOf(name), 1)
      options.expectedPacketReceived?.(name, data)
    } else {
      if (options.unexpectedPacketsLimit && unexpectedPacketsCount < options.unexpectedPacketsLimit) {
        options.unexpectedPacketReceived?.(name, data)
      }
      if (options.onUnexpectedPacketsLimitReached && unexpectedPacketsCount === options.unexpectedPacketsLimit) {
        options.onUnexpectedPacketsLimitReached?.()
      }
      unexpectedPacketsCount++
    }

    if (waitingPackets.length === 0) {
      resolve()
    }
  }

  return {
    addPacket (name: string, params: any) {
      if (packetHandler) {
        packetHandler(params, name)
      } else {
        queuedPackets.push({ name, params })
      }
    },

    async waitForPackets (packets: string[]) {
      if (isWaiting) {
        throw new Error('Already waiting for packets')
      }
      unexpectedPacketsCount = 0
      isWaiting = true

      try {
        await new Promise<void>(resolve => {
          const waitingPackets = [...packets]

          packetHandler = (data: any, name: string) => {
            handlePacket(data, name, waitingPackets, resolve)
          }

          // Process any queued packets
          for (const packet of queuedPackets) {
            handlePacket(packet.params, packet.name, waitingPackets, resolve)
          }
          queuedPackets.length = 0
        })
      } finally {
        isWaiting = false
        packetHandler = null
      }
    },
    stopWaiting () {
      isWaiting = false
      packetHandler = null
      queuedPackets.length = 0
    }
  }
}

// Only the legacy JSON format needs this: base64/hex replays are decoded by protodef,
// which already yields Buffers.
const restoreData = (json: any) => {
  if (typeof json !== 'object' || json === null) return json
  // Buffer is a Uint8Array, so Object.keys() on one materializes an index string per byte.
  if (ArrayBuffer.isView(json)) return json

  if (json.type === 'Buffer' && Array.isArray(json.data) && Object.keys(json).length === 2) {
    return Buffer.from(json.data)
  }

  for (const [key, value] of Object.entries(json)) {
    if (typeof value === 'object' && value !== null) {
      json[key] = restoreData(value)
    }
  }

  return json
}

export const VALID_REPLAY_EXTENSIONS = [`.${PACKETS_REPLAY_FILE_EXTENSION}`, `.${WORLD_STATE_FILE_EXTENSION}`]
